import { createConsumer } from '@rails/actioncable';
import SipClient from 'dashboard/helper/SipClient';
import CallRecordsApi from 'dashboard/api/callRecords';
import TelephonyConversationsAPI from 'dashboard/api/telephonyConversations';
import { emitter } from 'shared/helpers/mitt';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const CONNECTION_STATUS = Object.freeze({
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
});

const CALL_STATE = Object.freeze({
  IDLE: 'idle',
  RINGING: 'ringing',
  CONNECTED: 'connected',
});

// ponytail: UI-only; SipClient no conoce minimize
export function isMinimizeEligible(state) {
  if (state?.callState === CALL_STATE.CONNECTED) return true;
  if (state?.callState !== CALL_STATE.RINGING) return false;
  if (state?.callDirection === 'inbound') return true;
  return (
    state?.callDirection === 'outbound' &&
    state?.outboundStage !== 'prepared'
  );
}

function toggleInboundRingback(state, method) {
  if (state?.callState !== CALL_STATE.RINGING || state?.callDirection !== 'inbound') {
    return;
  }
  try {
    const client = SipClient.getInstance();
    if (client && typeof client[method] === 'function') client[method]();
  } catch (_e) {
    // ponytail: chrome already changed; autoplay/audio is best-effort
  }
}

/**
 * Inbound channel typing: Asterisk X-Chatwoot-Call-Type wins when present.
 * DID→whatsapp inference only when callType is null/blank (legacy softphone safety net).
 */
export function resolveInboundCallType({ callType, didNumber, inboxes } = {}) {
  const raw = callType == null ? '' : callType.toString().trim().toLowerCase();
  if (raw === 'sip' || raw === 'whatsapp') return raw;

  const didDigits = (didNumber || '').toString().replace(/\D/g, '');
  if (didDigits) {
    const list = Array.isArray(inboxes) ? inboxes : [];
    const matchesWhatsappDid = list.some(inbox => {
      if (inbox?.channel_type !== 'Channel::Whatsapp') return false;
      const inboxDigits = (inbox?.phone_number || '').toString().replace(/\D/g, '');
      return inboxDigits && inboxDigits === didDigits;
    });
    if (matchesWhatsappDid) return 'whatsapp';
  }
  return 'sip';
}

const normalizeMediaRoute = mediaRoute => {
  if (!mediaRoute || typeof mediaRoute !== 'object') return null;

  const type = (mediaRoute.type || mediaRoute.route_type || '')
    .toString()
    .trim()
    .toLowerCase();
  if (!type) return null;

  const rawInboxId = mediaRoute.inboxId || mediaRoute.inbox_id || null;
  return {
    type,
    inboxId: rawInboxId ? Number(rawInboxId) : null,
  };
};

const mediaRouteKey = mediaRoute => {
  const route = normalizeMediaRoute(mediaRoute);
  if (!route) return 'legacy';

  return `${route.type}:${route.inboxId || ''}`;
};

const isPbxEventsAuthoritative = (rootGetters, accountId) => {
  const isFeatureEnabled = rootGetters?.['accounts/isFeatureEnabledonAccount'];
  if (typeof isFeatureEnabled !== 'function' || !accountId) return false;
  return !!isFeatureEnabled(accountId, FEATURE_FLAGS.TELEPHONY_PBX_EVENTS_AUTHORITATIVE);
};


const currentAgentStamp = (rootGetters, extension) => {
  const user = rootGetters?.getCurrentUser || {};
  return {
    agent_id: user.id || null,
    agent_name: (user.available_name || user.name || '').toString() || null,
    sip_extension: extension || user.sip_extension || null,
  };
};

// Contact.phone_number requires E.164 (+[1-9]…). SIP From often arrives as digits-only.
const normalizePhoneE164 = raw => {
  const digits = (raw || '').toString().replace(/\D/g, '');
  if (!digits) return '';
  return `+${digits}`;
};

const humanizeCallResult = ({ reason, durationSec }) => {
  const r = (reason || '').toString();
  if (r === 'no_answer') return 'Llamada no contestada';
  if (r === 'busy') return 'Línea ocupada';
  if (r === 'rejected') return 'Llamada rechazada';
  if (r === 'cancelled') return 'Llamada cancelada';
  if (r === 'failed') return 'Llamada fallida';
  // llamadas conectadas/terminadas normalmente
  if (typeof durationSec === 'number' && durationSec > 0) return 'Llamada finalizada';
  return 'Llamada finalizada';
};

const state = {
  connectionStatus: CONNECTION_STATUS.DISCONNECTED,
  callState: CALL_STATE.IDLE,
  callDirection: null, // 'inbound' | 'outbound'
  remoteNumber: null,
  isMuted: false,
  callDuration: 0,
  isCallModalOpen: false,
  callUiMode: 'modal', // 'modal' | 'minimized'

  // outbound draft (click-to-call con selección de inbox SIP si aplica)
  outboundStage: 'idle', // 'idle' | 'prepared' | 'dialing'
  outboundDraftNumber: null,
  outboundDraftContactId: null,
  outboundSelectedSipInboxId: null,
  outboundNeedsInboxSelection: false,
  outboundMediaRoute: null,

  // internal wiring (no UI)
  _consumer: null,
  _subscription: null,
  _sipClientBound: false,
  _lastConfig: null,
  _gatewayReadyAt: null,
  _connectionRouteKey: null,

  // call context for logging
  _activeConversationId: null,
  _activeContactId: null,
  _activeInboxId: null,
  _activeCallType: 'sip',
  _activeCallId: null,
  _activeDidNumber: null,
  _activeWaCallId: null,

  transferPhase: 'idle',
  transferTargetName: '',
  transferErrorMessage: '',
  _whatsappCallDbId: null,
  _whatsappCallConversationId: null,
};

const getters = {
  connectionStatus: $state => $state.connectionStatus,
  callState: $state => $state.callState,
  callDirection: $state => $state.callDirection,
  remoteNumber: $state => $state.remoteNumber,
  isMuted: $state => $state.isMuted,
  callDuration: $state => $state.callDuration,
  isCallModalOpen: $state => $state.isCallModalOpen,
  callUiMode: $state => $state.callUiMode,
  isMinimizeEligible: $state => isMinimizeEligible($state),

  outboundStage: $state => $state.outboundStage,
  outboundDraftNumber: $state => $state.outboundDraftNumber,
  outboundDraftContactId: $state => $state.outboundDraftContactId,
  outboundSelectedSipInboxId: $state => $state.outboundSelectedSipInboxId,
  outboundNeedsInboxSelection: $state => $state.outboundNeedsInboxSelection,
  outboundMediaRoute: $state => $state.outboundMediaRoute,
  transferPhase: $state => $state.transferPhase,
  transferTargetName: $state => $state.transferTargetName,
  transferErrorMessage: $state => $state.transferErrorMessage,
  availableSipInboxes: (_$state, _$getters, _$rootState, rootGetters) => {
    const inboxes = rootGetters?.['inboxes/getInboxes'] || [];
    return (Array.isArray(inboxes) ? inboxes : []).filter(i => i?.channel_type === 'Channel::Sip');
  },
};

const mutations = {
  SET_CONNECTION_STATUS($state, status) {
    $state.connectionStatus = status;
  },
  SET_CALL_STATE($state, callState) {
    $state.callState = callState;
  },
  SET_CALL_DIRECTION($state, direction) {
    $state.callDirection = direction;
  },
  SET_REMOTE_NUMBER($state, remoteNumber) {
    $state.remoteNumber = remoteNumber;
  },
  SET_MUTED($state, isMuted) {
    $state.isMuted = isMuted;
  },
  SET_CALL_DURATION($state, durationSec) {
    $state.callDuration = durationSec;
  },
  SET_CALL_MODAL_OPEN($state, isOpen) {
    $state.isCallModalOpen = isOpen;
  },
  SET_CALL_UI_MODE($state, mode) {
    $state.callUiMode = mode === 'minimized' ? 'minimized' : 'modal';
  },

  SET_OUTBOUND_STAGE($state, stage) {
    $state.outboundStage = stage || 'idle';
  },
  SET_OUTBOUND_DRAFT_NUMBER($state, number) {
    $state.outboundDraftNumber = number || null;
  },
  SET_OUTBOUND_DRAFT_CONTACT_ID($state, contactId) {
    $state.outboundDraftContactId = contactId ? Number(contactId) : null;
  },
  SET_OUTBOUND_SELECTED_SIP_INBOX_ID($state, inboxId) {
    $state.outboundSelectedSipInboxId = inboxId ? Number(inboxId) : null;
  },
  SET_OUTBOUND_NEEDS_INBOX_SELECTION($state, needs) {
    $state.outboundNeedsInboxSelection = !!needs;
  },
  SET_OUTBOUND_MEDIA_ROUTE($state, mediaRoute) {
    $state.outboundMediaRoute = normalizeMediaRoute(mediaRoute);
  },

  SET_INTERNAL_CONSUMER($state, consumer) {
    $state._consumer = consumer;
  },
  SET_INTERNAL_SUBSCRIPTION($state, subscription) {
    $state._subscription = subscription;
  },
  SET_INTERNAL_SIPCLIENT_BOUND($state, bound) {
    $state._sipClientBound = bound;
  },
  SET_INTERNAL_LAST_CONFIG($state, config) {
    $state._lastConfig = config;
  },
  SET_INTERNAL_GATEWAY_READY_AT($state, ts) {
    $state._gatewayReadyAt = ts;
  },
  SET_INTERNAL_CONNECTION_ROUTE_KEY($state, routeKey) {
    $state._connectionRouteKey = routeKey || null;
  },

  SET_INTERNAL_ACTIVE_CONVERSATION_ID($state, conversationId) {
    $state._activeConversationId = conversationId;
  },
  SET_INTERNAL_ACTIVE_CONTACT_ID($state, contactId) {
    $state._activeContactId = contactId;
  },
  SET_INTERNAL_ACTIVE_INBOX_ID($state, inboxId) {
    $state._activeInboxId = inboxId;
  },
  SET_INTERNAL_ACTIVE_CALL_TYPE($state, callType) {
    $state._activeCallType = callType || 'sip';
  },
  SET_INTERNAL_ACTIVE_CALL_ID($state, callId) {
    $state._activeCallId = callId || null;
  },
  SET_INTERNAL_ACTIVE_DID_NUMBER($state, didNumber) {
    $state._activeDidNumber = didNumber || null;
  },
  SET_INTERNAL_ACTIVE_WA_CALL_ID($state, waCallId) {
    $state._activeWaCallId = waCallId || null;
  },
  SET_TRANSFER_PHASE($state, phase) {
    $state.transferPhase = phase || 'idle';
  },
  SET_TRANSFER_TARGET_NAME($state, name) {
    $state.transferTargetName = name || '';
  },
  SET_TRANSFER_ERROR_MESSAGE($state, message) {
    $state.transferErrorMessage = message || '';
  },
  RESET_TRANSFER_STATE($state) {
    $state.transferPhase = 'idle';
    $state.transferTargetName = '';
    $state.transferErrorMessage = '';
  },
  SET_INTERNAL_WHATSAPP_CALL_CONTEXT($state, { whatsappCallDbId, whatsappCallConversationId }) {
    $state._whatsappCallDbId = whatsappCallDbId || null;
    $state._whatsappCallConversationId = whatsappCallConversationId || null;
  },
};

const postTransferCallLog = async ({ rootGetters, state, result, targetAgent }) => {
  const wootAxios = window?.axios;
  const accountId = rootGetters.getCurrentAccountId;
  const conversationId = state._activeConversationId;
  const callId = (state._activeCallId || '').toString().trim();
  if (!wootAxios || !accountId || !conversationId || !callId) return;
  const current = rootGetters.getCurrentUser || {};
  const fromName = (current.available_name || current.name || '').toString();
  const toName = (targetAgent?.available_name || targetAgent?.name || '').toString();
  try {
    await wootAxios.post(`/api/v1/accounts/${accountId}/conversations/${conversationId}/messages`, {
      content: `📞 ${result} • ${fromName} → ${toName}`,
      private: true,
      content_type: 'call_log',
      content_attributes: {
        call_details: {
          event: 'transfer',
          source: 'softphone',
          call_type: state._activeCallType || 'sip',
          provider: 'asterisk18',
          call_id: callId,
          result,
          from_agent_id: current.id || null,
          from_agent_name: fromName || null,
          from_sip_extension: current.sip_extension || null,
          to_agent_id: targetAgent?.id || null,
          to_agent_name: toName || null,
          to_sip_extension: (targetAgent?.sip_extension || '').toString().trim() || null,
        },
      },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[telephony] transfer call_log failed', e?.message);
  }
};

const actions = {
  bindSipClientListeners({ commit, dispatch, state: $state, rootGetters }) {
    if ($state._sipClientBound) return;
    const sipClient = SipClient.getInstance();

    sipClient.setAvailabilityProvider(() => rootGetters.getCurrentUserAvailability);

    sipClient.on('connection:status', ({ status }) => {
      commit('SET_CONNECTION_STATUS', status);
    });

    sipClient.on('registration:status', ({ status }) => {
      if (status === 'registered') {
        commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTED);
        const accountId = rootGetters.getCurrentAccountId;
        if (accountId && window?.axios) {
          window.axios.post('/api/v1/profile/telephony_queue_sync', { account_id: accountId }).catch(() => {});
        }
      }
      if (status === 'unregistered' && $state.connectionStatus !== CONNECTION_STATUS.CONNECTING) {
        commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTING);
      }
    });

    sipClient.on('call:incoming', async ({ remoteNumber, callType, callId, didNumber, waCallId, transferContext }) => {
      commit('SET_REMOTE_NUMBER', remoteNumber || null);
      commit('SET_CALL_DIRECTION', 'inbound');
      commit('SET_CALL_STATE', CALL_STATE.RINGING);
      commit('SET_CALL_UI_MODE', 'modal');
      commit('SET_CALL_MODAL_OPEN', true);
      commit('RESET_TRANSFER_STATE');
      const inboxes = rootGetters?.['inboxes/getInboxes'] || [];
      const headerMissing = callType == null || !callType.toString().trim();
      const normalizedCallType = resolveInboundCallType({ callType, didNumber, inboxes });
      if (headerMissing && normalizedCallType === 'whatsapp') {
        // eslint-disable-next-line no-console
        console.info('[WSP_CALL_TRACE] inbound:call_type_inferred_by_did', { didNumber });
      }
      const normalizedCallId = (callId || '').toString().trim() || null;
      if (!normalizedCallId) {
        // eslint-disable-next-line no-console
        console.warn('[telephony] inbound missing X-Chatwoot-Call-Id; PBX UNIQUEID correlation may fail');
      }
      commit('SET_INTERNAL_ACTIVE_CALL_TYPE', normalizedCallType);
      commit('SET_INTERNAL_ACTIVE_CALL_ID', normalizedCallId);
      commit('SET_INTERNAL_ACTIVE_DID_NUMBER', didNumber || null);
      commit('SET_INTERNAL_ACTIVE_WA_CALL_ID', waCallId || null);

      // Timbrado entrante: reutiliza el ringback (WebAudio) del SipClient para sonar como llamada normal.
      // Nota: el audio puede requerir un gesto del usuario si el navegador bloquea autoplay.
      sipClient.startRingback();

      // Crear conversación/mensaje en inbox SIP para llamadas entrantes.
      // Nota: debe ser 1 conversación por llamada (igual que outbound), incluso si el número se repite.
      if (transferContext?.conversationId) {
        commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', transferContext.conversationId);
        return;
      }

      try {
        await dispatch('ENSURE_INBOUND_CALL_CONTEXT', {
          remoteNumber,
          callType: normalizedCallType,
          callId: normalizedCallId,
          didNumber,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[telephony] no se pudo preparar conversación inbound', e);
      }
    });

    sipClient.on('call:state', ({ state: callState, remoteNumber, direction }) => {
      if (remoteNumber) commit('SET_REMOTE_NUMBER', remoteNumber);
      if (direction) commit('SET_CALL_DIRECTION', direction);
      if (callState === 'idle') {
        sipClient.stopRingback();
        commit('SET_CALL_STATE', CALL_STATE.IDLE);
        commit('SET_CALL_DIRECTION', null);
        commit('SET_CALL_DURATION', 0);
        commit('SET_MUTED', false);
        commit('SET_CALL_UI_MODE', 'modal');
        commit('SET_CALL_MODAL_OPEN', false);
        commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', null);
        commit('SET_INTERNAL_ACTIVE_CONTACT_ID', null);
        commit('SET_INTERNAL_ACTIVE_INBOX_ID', null);
        commit('SET_INTERNAL_ACTIVE_CALL_TYPE', 'sip');
        commit('SET_INTERNAL_ACTIVE_CALL_ID', null);
        commit('SET_INTERNAL_ACTIVE_DID_NUMBER', null);
        commit('SET_INTERNAL_ACTIVE_WA_CALL_ID', null);
        commit('RESET_TRANSFER_STATE');

        // reset outbound draft
        commit('SET_OUTBOUND_STAGE', 'idle');
        commit('SET_OUTBOUND_DRAFT_NUMBER', null);
        commit('SET_OUTBOUND_DRAFT_CONTACT_ID', null);
        commit('SET_OUTBOUND_SELECTED_SIP_INBOX_ID', null);
        commit('SET_OUTBOUND_NEEDS_INBOX_SELECTION', false);
        commit('SET_OUTBOUND_MEDIA_ROUTE', null);
        return;
      }
      if (callState === 'ringing') {
        commit('SET_CALL_STATE', CALL_STATE.RINGING);
        if ($state.callUiMode !== 'minimized') {
          commit('SET_CALL_MODAL_OPEN', true);
        }
        return;
      }
      if (callState === 'connected') {
        sipClient.stopRingback();
        commit('SET_CALL_STATE', CALL_STATE.CONNECTED);
        if ($state.callUiMode !== 'minimized') {
          commit('SET_CALL_MODAL_OPEN', true);
        }
      }
    });

    sipClient.on('call:duration', ({ durationSec }) => {
      commit('SET_CALL_DURATION', durationSec || 0);
    });

    sipClient.on('call:muted', ({ isMuted }) => {
      commit('SET_MUTED', !!isMuted);
    });

    sipClient.on(
      'call:ended',
      async ({ direction, remoteNumber, durationSec, reason, sipStatusCode, sipReasonPhrase }) => {
      try {
        sipClient.stopRingback();
        const label = humanizeCallResult({ reason, durationSec });
        const who = (remoteNumber || $state.remoteNumber || '').toString().trim();
        const toastMsg = who ? `${label}: ${who}` : label;
        emitter.emit('newToastMessage', toastMsg);

        await dispatch('LOG_CALL', {
          direction,
          remoteNumber,
          durationSec,
          reason,
          sipStatusCode,
          sipReasonPhrase,
          label,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[telephony] call log failed', e);
      }
      }
    );

    commit('SET_INTERNAL_SIPCLIENT_BOUND', true);
  },

  async CONNECT({ commit, dispatch, rootGetters, state: $state }, { mediaRoute } = {}) {
    dispatch('bindSipClientListeners');

    const accountId = rootGetters.getCurrentAccountId;
    if (!accountId) return;

    const normalizedMediaRoute = normalizeMediaRoute(mediaRoute);
    const routeKey = mediaRouteKey(normalizedMediaRoute);
    const isWhatsappMediaRoute = normalizedMediaRoute?.type === 'whatsapp';
    const isFeatureEnabled = rootGetters?.['accounts/isFeatureEnabledonAccount'];
    const hasSipFeature =
      typeof isFeatureEnabled === 'function'
        ? !!isFeatureEnabled(accountId, FEATURE_FLAGS.CHANNEL_SIP)
        : false;
    const hasWhatsappCallingFeature =
      typeof isFeatureEnabled === 'function'
        ? !!isFeatureEnabled(accountId, FEATURE_FLAGS.WHATSAPP_CALLING)
        : false;

    // Si SIP no está habilitado para esta cuenta, no intentar suscribirse al gateway.
    if (!hasSipFeature && !(isWhatsappMediaRoute && hasWhatsappCallingFeature)) {
      if (isWhatsappMediaRoute) {
        commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.ERROR);
        throw new Error('WhatsApp Calling no está habilitado para esta cuenta.');
      }
      return;
    }

    // Avoid duplicate connections
    const sipClientForRoute = SipClient.getInstance();
    if (
      $state._consumer &&
      $state._subscription &&
      $state._connectionRouteKey === routeKey &&
      $state.connectionStatus !== CONNECTION_STATUS.ERROR
    ) {
      // Tras WS drop, isRegistered puede estar false aunque sip.js siga Registered.
      const recovered = await sipClientForRoute.recoverRegistration();
      if (recovered) {
        commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTED);
        return;
      }
      // Soft-recover falló → reconnect completo abajo (no hacer scheduleRegisterRetry+return).
    }

    if ($state._subscription) {
      try {
        $state._subscription.unsubscribe();
      } catch {
        // ignore
      }
    }
    if ($state._consumer) {
      try {
        $state._consumer.disconnect();
      } catch {
        // ignore
      }
    }
    try {
      await SipClient.getInstance().disconnect({ preserveIntent: true });
    } catch {
      // ignore
    }
    commit('SET_INTERNAL_SUBSCRIPTION', null);
    commit('SET_INTERNAL_CONSUMER', null);
    commit('SET_INTERNAL_LAST_CONFIG', null);
    commit('SET_INTERNAL_GATEWAY_READY_AT', null);

    commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTING);
    commit('SET_INTERNAL_CONNECTION_ROUTE_KEY', routeKey);

    const wootAxios = window?.axios;
    if (!wootAxios) {
      commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.ERROR);
      throw new Error('Axios no inicializado (window.axios). Revisa el bootstrap del dashboard.');
    }

    let token = null;
    try {
      const tokenRes = await wootAxios.get('/api/v1/profile/telephony_token', {
        params: { account_id: accountId },
      });
      token = tokenRes?.data?.token || null;
    } catch (e) {
      commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.ERROR);
      throw e;
    }
    if (!token) {
      commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.ERROR);
      throw new Error('No se pudo obtener telephony_token (verifica TELEPHONY_JWT_SECRET)');
    }

    const { websocketURL = '' } = window.chatwootConfig || {};
    const consumer = createConsumer(websocketURL ? `${websocketURL}/cable` : undefined);
    commit('SET_INTERNAL_CONSUMER', consumer);

    const subscriptionParams = { channel: 'TelephonyGatewayChannel', token };
    if (isWhatsappMediaRoute) {
      subscriptionParams.route_type = 'whatsapp';
      subscriptionParams.inbox_id = normalizedMediaRoute.inboxId;
    }

    const subscription = consumer.subscriptions.create(
      subscriptionParams,
      {
        connected: () => {
          // AcciónCable conectado; esperamos gateway:ready
          // eslint-disable-next-line no-console
          console.log('[telephony] ActionCable connected (TelephonyGatewayChannel)');

          // Si en X segundos no llega gateway:ready, marcamos error para no quedar “colgados”.
          setTimeout(() => {
            if (!$state._lastConfig) {
              commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.ERROR);
              // eslint-disable-next-line no-console
              console.error(
                '[telephony] Timeout esperando gateway:ready. Revisa que TelephonyGatewayChannel no esté rechazando la suscripción y que exista SIP mapping para el agente.'
              );
            }
          }, 8000);
        },
        disconnected: () => {
          commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTING);
          // eslint-disable-next-line no-console
          console.log('[telephony] ActionCable disconnected (TelephonyGatewayChannel)');
        },
        rejected: () => {
          commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.ERROR);
          // eslint-disable-next-line no-console
          console.error(
            '[telephony] Subscription rejected. Revisa: TELEPHONY_JWT_SECRET, SIP inbox en la cuenta, y account_users.sip_extension/sip_auth_password.'
          );
        },
        received: async payload => {
          // eslint-disable-next-line no-console
          console.log('[telephony] received from TelephonyGatewayChannel', payload);
          if (payload?.event !== 'gateway:ready') return;
          const data = payload?.data || {};
          commit('SET_INTERNAL_LAST_CONFIG', data);
          commit('SET_INTERNAL_GATEWAY_READY_AT', Date.now());
          commit('SET_INTERNAL_CONNECTION_ROUTE_KEY', routeKey);
          try {
            await dispatch('CONNECT_FROM_GATEWAY_READY', data);
          } catch (e) {
            commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.ERROR);
          }
        },
      }
    );

    commit('SET_INTERNAL_SUBSCRIPTION', subscription);
  },

  async CONNECT_FROM_GATEWAY_READY({ commit }, gatewayConfig) {
    const sipClient = SipClient.getInstance();
    try {
      await sipClient.connect(gatewayConfig);
      commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTED);
    } catch (e) {
      commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.ERROR);
      throw e;
    }
  },

  async ENSURE_CONNECTED({ dispatch, commit, state: $state }, { timeoutMs = 15000, mediaRoute } = {}) {
    const routeKey = mediaRouteKey(mediaRoute);
    const sipClient = SipClient.getInstance();

    // Resincroniza flag local vs estado real del Registerer (post WS drop).
    if (
      $state._connectionRouteKey === routeKey &&
      sipClient.syncRegistrationFromRegisterer()
    ) {
      commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTED);
      return;
    }

    if (
      $state.connectionStatus === CONNECTION_STATUS.CONNECTED &&
      $state._connectionRouteKey === routeKey &&
      sipClient.isRegistered
    ) {
      return;
    }

    try {
      await dispatch('CONNECT', { mediaRoute });
    } catch (e) {
      throw e;
    }

    const start = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const client = SipClient.getInstance();
      if (client.syncRegistrationFromRegisterer()) {
        commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTED);
      }
      if (
        $state.connectionStatus === CONNECTION_STATUS.CONNECTED &&
        $state._connectionRouteKey === routeKey &&
        client.isRegistered
      ) {
        return;
      }
      if ($state.connectionStatus === CONNECTION_STATUS.ERROR) {
        throw new Error('Telephony en estado ERROR (revisa consola / ActionCable / gateway:ready)');
      }
      if (Date.now() - start > timeoutMs) {
        // Último intento soft antes de fallar
        const recovered = await client.recoverRegistration();
        if (recovered && $state._connectionRouteKey === routeKey) {
          commit('SET_CONNECTION_STATUS', CONNECTION_STATUS.CONNECTED);
          return;
        }
        throw new Error('Telephony aún no está conectada/registrada (timeout esperando gateway:ready/REGISTERED)');
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, 250));
    }
  },

  async ENSURE_SIP_INBOX_ID({ dispatch, rootGetters }, { preferredInboxId, didNumber } = {}) {
    // IMPORTANT: No usar el inbox actual de la ruta. El call log debe ir al inbox SIP, no al inbox desde donde se dispara el click-to-call.
    // (Si usamos el inbox actual, terminamos creando mensajes en conversaciones "normales", que fue el bug reportado).

    // Asegurar que inboxes estén cargados
    let inboxes = rootGetters?.['inboxes/getInboxes'] || [];
    if (!Array.isArray(inboxes) || inboxes.length === 0) {
      // eslint-disable-next-line no-console
      console.log('[telephony] inboxes vacíos, intentando cargar inboxes para resolver inbox SIP');
      try {
        // Acción namespaced: inboxes/get
        await dispatch('inboxes/get', null, { root: true });
      } catch (e) {
        // ignore y seguimos con lo que haya
      }
      inboxes = rootGetters?.['inboxes/getInboxes'] || [];
    }

    const sipChannelId = rootGetters?.getCurrentUser?.sip_channel_id || null;

    // 0) Si viene un inbox SIP explícito (selección outbound), úsalo.
    if (preferredInboxId) {
      const preferred = Number(preferredInboxId);
      const selected = inboxes.find(i => Number(i?.id) === preferred);
      if (!selected) {
        throw new Error('Inbox SIP seleccionado no existe o no está disponible para el agente');
      }
      if (selected.channel_type !== 'Channel::Sip') {
        throw new Error('Inbox seleccionado no es SIP');
      }
      return preferred;
    }

    // 0b) Inbound PSTN: match Channel::Sip by DID (same resolve as PBX ingest).
    const didDigits = (didNumber || '').toString().replace(/\D/g, '');
    if (didDigits) {
      const byDid = inboxes.find(i => {
        if (i?.channel_type !== 'Channel::Sip') return false;
        const inboxDigits = (i?.phone_number || '').toString().replace(/\D/g, '');
        return inboxDigits && inboxDigits === didDigits;
      });
      if (byDid?.id) return Number(byDid.id);
    }

    // 1) Si el agente tiene sip_channel_id, preferimos el inbox cuyo channel_id lo matchee
    const sipInboxExact = inboxes.find(
      i => i?.channel_type === 'Channel::Sip' && (sipChannelId ? Number(i.channel_id) === Number(sipChannelId) : true)
    );
    if (sipInboxExact?.id) return Number(sipInboxExact.id);

    // 2) Fallback: cualquier inbox SIP asignado al usuario
    const sipInboxAny = inboxes.find(i => i?.channel_type === 'Channel::Sip');
    if (sipInboxAny?.id) return Number(sipInboxAny.id);

    throw new Error('No se pudo resolver inbox SIP (no hay inbox con channel_type=Channel::Sip en store)');
  },

  async ENSURE_CONTACT_ID({ rootGetters }, { phoneNumber, contactId }) {
    if (contactId) return contactId;
    const wootAxios = window?.axios;
    if (!wootAxios) throw new Error('Axios no inicializado (window.axios)');

    const q = normalizePhoneE164(phoneNumber);
    if (!q) throw new Error('Número inválido');

    const accountId = rootGetters.getCurrentAccountId;
    const searchContact = async () => {
      const searchRes = await wootAxios.get(`/api/v1/accounts/${accountId}/contacts/search`, {
        params: { q, page: 1, include_contact_inboxes: false },
      });
      const contacts =
        searchRes?.data?.payload || searchRes?.data?.payload?.data || searchRes?.data?.data || [];
      const first = Array.isArray(contacts) ? contacts[0] : null;
      return first?.id || null;
    };

    const existingId = await searchContact();
    if (existingId) return existingId;

    // Crear contacto mínimo. create.json.jbuilder → { payload: { contact: { id } } }
    try {
      const createRes = await wootAxios.post(`/api/v1/accounts/${accountId}/contacts`, {
        name: q,
        phone_number: q,
      });
      const createdId =
        createRes?.data?.payload?.contact?.id ||
        createRes?.data?.payload?.id ||
        createRes?.data?.id;
      if (!createdId) {
        throw new Error('No se pudo resolver contact_id tras crear contacto');
      }
      return createdId;
    } catch (e) {
      // Uniqueness / race: contact appeared between search and create.
      if (e?.response?.status === 422) {
        const racedId = await searchContact();
        if (racedId) return racedId;
      }
      throw e;
    }
  },

  async ENSURE_CONVERSATION_ID(
    { commit, rootGetters, dispatch, state: $state },
    { phoneNumber, contactId, direction = 'outbound', callType = 'sip', callId = null, didNumber = null }
  ) {
    if (isPbxEventsAuthoritative(rootGetters, rootGetters.getCurrentAccountId)) {
      return null;
    }
    const wootAxios = window?.axios;
    if (!wootAxios) throw new Error('Axios no inicializado (window.axios)');

    const accountId = rootGetters.getCurrentAccountId;
    const inboxId = await dispatch('ENSURE_SIP_INBOX_ID', { didNumber });
    commit('SET_INTERNAL_ACTIVE_INBOX_ID', inboxId);

    const sourceId = (phoneNumber || '').toString().trim();
    if (!sourceId) throw new Error('Número inválido (source_id vacío)');

    // Crear conversación en el inbox SIP con un mensaje inicial.
    const agentExtension =
      $state?._lastConfig?.extension ||
      rootGetters?.getCurrentUser?.sip_extension ||
      null;

    const isInbound = direction === 'inbound';
    const normalizedCallType = (callType || 'sip').toString();
    // Bridge to PBX ingest find_conversation_by_call_log (same Asterisk UNIQUEID).
    const resolvedCallId = (callId || $state._activeCallId || '').toString().trim() || null;

    const initialMessage = {
      content: isInbound ? `📞 Llamada entrante de ${phoneNumber}` : `📞 Llamada saliente a ${phoneNumber}`,
      private: true,
      content_type: 'call_log',
      content_attributes: {
        call_details: {
          call_type: normalizedCallType,
          provider: 'asterisk18',
          ...(resolvedCallId ? { call_id: resolvedCallId } : {}),
          direction: isInbound ? 'inbound' : 'outbound',
          from: isInbound ? phoneNumber : agentExtension,
          to: isInbound ? agentExtension : phoneNumber,
          ts: Date.now(),
          result: 'started',
          ...currentAgentStamp(rootGetters, agentExtension),
        },
      },
    };

    // Inbound SIP multi-ring: atomic ensure by Asterisk call_id (find-or-create + CallRecord claim).
    if (isInbound && resolvedCallId) {
      try {
        const ensureRes = await TelephonyConversationsAPI.ensureByCallId({
          callId: resolvedCallId,
          accountId,
          inboxId,
          contactId,
          sourceId,
          message: initialMessage,
        });
        const convoId = ensureRes?.data?.id || ensureRes?.data?.display_id;
        if (convoId) {
          commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', convoId);
          if (ensureRes?.data?.inbox_id) {
            commit('SET_INTERNAL_ACTIVE_INBOX_ID', Number(ensureRes.data.inbox_id));
          }
          commit('SET_INTERNAL_ACTIVE_CALL_TYPE', normalizedCallType);
          return convoId;
        }
      } catch (e) {
        const status = e?.response?.status;
        // eslint-disable-next-line no-console
        console.warn('[telephony] ensure_by_call_id failed; creating conversation', {
          callId: resolvedCallId,
          status,
          message: e?.message,
        });
      }
    }

    const createPayload = {
      inbox_id: inboxId,
      contact_id: contactId,
      source_id: sourceId,
      message: initialMessage,
    };
    if (!isInbound) {
      const outboundAssigneeId = rootGetters?.getCurrentUser?.id;
      if (outboundAssigneeId) createPayload.assignee_id = outboundAssigneeId;
    }

    const createRes = await wootAxios.post(
      `/api/v1/accounts/${accountId}/conversations`,
      createPayload
    );

    const convo = createRes?.data;
    if (!convo?.id) throw new Error('No se pudo crear conversación SIP');
    commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', convo.id);
    commit('SET_INTERNAL_ACTIVE_CALL_TYPE', normalizedCallType);
    return convo.id;
  },

  async LOG_CALL(
    { state: $state, rootGetters, commit },
    { direction, remoteNumber, durationSec, reason, sipStatusCode, sipReasonPhrase, label }
  ) {
    const conversationId = $state._activeConversationId;
    const accountId = rootGetters.getCurrentAccountId;
    if (isPbxEventsAuthoritative(rootGetters, accountId)) {
      commit('SET_INTERNAL_WHATSAPP_CALL_CONTEXT', { whatsappCallDbId: null, whatsappCallConversationId: null });
      commit('SET_INTERNAL_ACTIVE_CALL_ID', null);
      commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', null);
      return;
    }
    // Hybrid C: inbound closing ledger is PBX-only (avoid finalizada + contestada dual-write).
    const resolvedDirection = (direction || $state.callDirection || 'outbound').toString();
    if (resolvedDirection === 'inbound') {
      commit('SET_INTERNAL_WHATSAPP_CALL_CONTEXT', {
        whatsappCallDbId: null,
        whatsappCallConversationId: null,
      });
      commit('SET_INTERNAL_ACTIVE_CALL_ID', null);
      commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', null);
      return;
    }
    if (!conversationId) return;

    const wootAxios = window?.axios;
    if (!wootAxios) return;

    const now = Date.now();

    const capturedCallType = $state._activeCallType || 'sip';
    const capturedCallId = ($state._activeCallId || `wsp-${now}`).toString();
    const capturedWhatsappCallDbId = $state._whatsappCallDbId || null;
    const capturedWhatsappCallConversationId = $state._whatsappCallConversationId || null;

    const callDetails = {
      call_type: capturedCallType,
      provider: 'asterisk18',
      call_id: capturedCallId,
      direction: direction || $state.callDirection || 'outbound',
      from: null,
      to: remoteNumber || $state.remoteNumber,
      ts: now,
      duration_sec: typeof durationSec === 'number' ? durationSec : $state.callDuration,
      result: reason || 'terminated',
      sip_status_code: sipStatusCode || null,
      sip_reason_phrase: sipReasonPhrase || null,
    };

    const labelText = (label || humanizeCallResult({ reason: callDetails.result, durationSec: callDetails.duration_sec }))
      .toString()
      .trim();

    await wootAxios.post(`/api/v1/accounts/${accountId}/conversations/${conversationId}/messages`, {
      content: `📞 ${labelText} • ${callDetails.duration_sec}s`,
      private: true,
      content_type: 'call_log',
      content_attributes: { call_details: callDetails },
      additional_attributes: {},
    });

    if (capturedCallType === 'sip') {
      try {
        const endedAtEpoch = Math.floor(now / 1000);
        const answeredAtEpoch = callDetails.duration_sec
          ? endedAtEpoch - callDetails.duration_sec
          : null;
        await CallRecordsApi.logSipCall(conversationId, {
          call_id: capturedCallId,
          direction: callDetails.direction,
          status: callDetails.result || 'terminated',
          duration_seconds: callDetails.duration_sec || 0,
          ended_at: endedAtEpoch,
          started_at: answeredAtEpoch || endedAtEpoch,
          answered_at: answeredAtEpoch,
          metadata: {
            mode: 'sip_client_no_ari',
            dial_via: 'telephony_store_sip_client',
            result: callDetails.result,
            sip_status_code: callDetails.sip_status_code,
            sip_reason_phrase: callDetails.sip_reason_phrase,
            from: callDetails.from,
            to: callDetails.to,
          },
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[WSP_CALL_TRACE] log_sip_call:failed', {
          conversationId,
          callId: capturedCallId,
          error: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
        });
      }
    }

    if (
      capturedCallType === 'whatsapp' &&
      capturedWhatsappCallDbId &&
      capturedWhatsappCallConversationId
    ) {
      try {
        // eslint-disable-next-line no-console
        console.info('[WSP_CALL_TRACE] terminate_sync:start', {
          whatsappCallDbId: capturedWhatsappCallDbId,
          whatsappCallConversationId: capturedWhatsappCallConversationId,
          duration_seconds: callDetails.duration_sec,
        });
        const endedAtEpoch = Math.floor(now / 1000);
        const answeredAtEpoch = callDetails.duration_sec
          ? endedAtEpoch - callDetails.duration_sec
          : null;
        await CallRecordsApi.terminate(capturedWhatsappCallConversationId, {
          id: capturedWhatsappCallDbId,
          call_id: capturedCallId,
          duration_seconds: callDetails.duration_sec,
          ended_at: endedAtEpoch,
          answered_at: answeredAtEpoch,
        });
        // eslint-disable-next-line no-console
        console.info('[WSP_CALL_TRACE] terminate_sync:ok', {
          whatsappCallDbId: capturedWhatsappCallDbId,
          whatsappCallConversationId: capturedWhatsappCallConversationId,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[WSP_CALL_TRACE] terminate_sync:failed', {
          whatsappCallDbId: capturedWhatsappCallDbId,
          whatsappCallConversationId: capturedWhatsappCallConversationId,
          error: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
        });
      }
    } else if (capturedCallType === 'whatsapp' && !capturedWhatsappCallDbId) {
      const logConvoId = capturedWhatsappCallConversationId || conversationId;
      const callDir = direction || $state.callDirection || 'unknown';
      const isRejectedInbound =
        callDetails.result === 'rejected' && callDetails.direction === 'inbound';
      if (!isRejectedInbound) {
      try {
        // eslint-disable-next-line no-console
        console.info('[WSP_CALL_TRACE] log_inbound:start', {
          conversationId: logConvoId,
          direction: callDir,
          duration_seconds: callDetails.duration_sec,
        });
        const inboundEndedAt = Math.floor(now / 1000);
        const inboundAnsweredAt = callDetails.duration_sec
          ? inboundEndedAt - callDetails.duration_sec
          : null;
        await CallRecordsApi.logInbound(logConvoId, {
          call_id: capturedCallId,
          duration_seconds: callDetails.duration_sec,
          ended_at: inboundEndedAt,
          started_at: inboundAnsweredAt || inboundEndedAt,
          answered_at: inboundAnsweredAt,
          metadata: {
            mode: 'sip_client_no_ari',
            dial_via: 'telephony_store_sip_client',
            caller: callDetails.to,
            result: callDetails.result,
            sip_status_code: callDetails.sip_status_code,
            sip_reason_phrase: callDetails.sip_reason_phrase,
          },
        });
        // eslint-disable-next-line no-console
        console.info('[WSP_CALL_TRACE] log_inbound:ok', { conversationId: logConvoId });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[WSP_CALL_TRACE] log_inbound:failed', {
          conversationId: logConvoId,
          error: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
        });
      }
      }
    }

    commit('SET_INTERNAL_WHATSAPP_CALL_CONTEXT', { whatsappCallDbId: null, whatsappCallConversationId: null });
    commit('SET_INTERNAL_ACTIVE_CALL_ID', null);
    commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', null);
  },

  async DIAL({ commit, dispatch, rootGetters, state: $state }, phoneNumber) {
    const sipClient = SipClient.getInstance();
    const payload = typeof phoneNumber === 'object' ? phoneNumber : { phoneNumber };
    const number = (payload.phoneNumber || '').toString().trim();
    const preferredSipInboxId = payload.sipInboxId || null;
    const callType = (payload.callType || 'sip').toString().trim().toLowerCase() || 'sip';
    const mediaRoute = normalizeMediaRoute(payload.mediaRoute);
    const isExplicitWhatsappRoute = callType === 'whatsapp' && mediaRoute?.type === 'whatsapp' && mediaRoute?.inboxId;
    const callId = (payload.callId || `wsp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)
      .toString()
      .trim();

    // No abrir modal de "Llamando…" hasta tener SIP usable; PREPARE_DIAL ya puede mostrar draft.
    commit('SET_OUTBOUND_STAGE', 'dialing');
    commit('SET_REMOTE_NUMBER', number || null);
    commit('SET_CALL_DIRECTION', 'outbound');

    try {
      await dispatch('ENSURE_CONNECTED', { mediaRoute });
      commit('SET_CALL_UI_MODE', 'modal');
      commit('SET_CALL_MODAL_OPEN', true);
      commit('SET_CALL_STATE', CALL_STATE.RINGING);

      // Para PSTN se crea conversación SIP; para WhatsApp se reutiliza conversación WhatsApp.
      const inboxId = isExplicitWhatsappRoute
        ? mediaRoute.inboxId
        : await dispatch('ENSURE_SIP_INBOX_ID', { preferredInboxId: preferredSipInboxId });
      const contactId = await dispatch('ENSURE_CONTACT_ID', {
        phoneNumber: number,
        contactId: payload.contactId,
      });
      commit('SET_INTERNAL_ACTIVE_CONTACT_ID', contactId);
      commit('SET_INTERNAL_ACTIVE_INBOX_ID', inboxId);
      commit('SET_INTERNAL_ACTIVE_CALL_TYPE', callType);
      commit('SET_INTERNAL_ACTIVE_CALL_ID', callId);

      if (callType === 'whatsapp') {
        const whatsappConversationId = $state._whatsappCallConversationId || payload.conversationDisplayId || null;
        if (whatsappConversationId) {
          commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', whatsappConversationId);
          try {
            const accountId = rootGetters.getCurrentAccountId;
            const wootAxios = window?.axios;
            if (wootAxios && accountId) {
              await wootAxios.post(`/api/v1/accounts/${accountId}/conversations/${whatsappConversationId}/messages`, {
                content: `📞 Llamada saliente a ${number}`,
                private: true,
                content_type: 'call_log',
                content_attributes: {
                  call_details: {
                    call_type: 'whatsapp',
                    provider: 'asterisk18',
                    call_id: callId,
                    direction: 'outbound',
                    from: rootGetters?.getCurrentUser?.sip_extension || null,
                    to: number,
                    ts: Date.now(),
                    result: 'started',
                    ...currentAgentStamp(rootGetters, rootGetters?.getCurrentUser?.sip_extension),
                  },
                },
              });
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[WSP_CALL_TRACE] dial:outbound_started_msg_error', e?.message);
          }
        } else {
          commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', null);
          // eslint-disable-next-line no-console
          console.warn('[WSP_CALL_TRACE] dial:missing_whatsapp_conversation_context');
        }
      } else {
        await dispatch('ENSURE_CONVERSATION_ID', {
          phoneNumber: number,
          contactId,
          direction: 'outbound',
          callType,
        });
      }

      // DID a mostrar hacia PSTN: usamos phone_number del inbox SIP (Channel::Sip.phone_number)
      let allInboxes = rootGetters?.['inboxes/getInboxes'] || [];
      let selectedInbox = (Array.isArray(allInboxes) ? allInboxes : []).find(i => Number(i?.id) === Number(inboxId));
      if (!selectedInbox) {
        try {
          await dispatch('inboxes/get', null, { root: true });
        } catch {
          // ignore
        }
        allInboxes = rootGetters?.['inboxes/getInboxes'] || [];
        selectedInbox = (Array.isArray(allInboxes) ? allInboxes : []).find(i => Number(i?.id) === Number(inboxId));
      }
      const didNumber =
        (selectedInbox?.phone_number || $state?._lastConfig?.did_number || '')
          .toString()
          .trim() || null;

      // Defensa final: resync por si un WS drop dejó el flag desfasado.
      if (!sipClient.syncRegistrationFromRegisterer() && !sipClient.isRegistered) {
        const recovered = await sipClient.recoverRegistration();
        if (!recovered) throw new Error('SIP no registrado');
      }

      await sipClient.dial(number, { didNumber, callType, callId });
    } catch (e) {
      // Modal ya abierto en "Llamando…": si no hay sesión SIP, deshacer UI (evita modal zombie).
      if (!sipClient.currentSession) {
        await dispatch('CANCEL_DRAFT');
      }
      throw e;
    }
  },

  async PREPARE_DIAL({ commit, getters, dispatch }, payload) {
    const p = typeof payload === 'object' ? payload : { phoneNumber: payload };
    const number = (p.phoneNumber || '').toString().trim();
    const callType = (p.callType || 'sip').toString().trim().toLowerCase() || 'sip';
    const mediaRoute = normalizeMediaRoute(p.mediaRoute);
    const isExplicitWhatsappRoute = callType === 'whatsapp' && mediaRoute?.type === 'whatsapp' && mediaRoute?.inboxId;
    if (!number) return;

    let sipInboxes = [];
    if (!isExplicitWhatsappRoute) {
      // Asegurar inboxes cargados para poder listar inbox SIP disponibles
      sipInboxes = getters.availableSipInboxes;
      if (!Array.isArray(sipInboxes) || sipInboxes.length === 0) {
        try {
          await dispatch('inboxes/get', null, { root: true });
        } catch {
          // ignore
        }
        sipInboxes = getters.availableSipInboxes;
      }
    } else {
      try {
        await dispatch('inboxes/get', null, { root: true });
      } catch {
        // ignore
      }
    }

    const needsSelection = !isExplicitWhatsappRoute && (Array.isArray(sipInboxes) ? sipInboxes.length : 0) > 1;

    commit('SET_OUTBOUND_STAGE', 'prepared');
    commit('SET_OUTBOUND_DRAFT_NUMBER', number);
    commit('SET_OUTBOUND_DRAFT_CONTACT_ID', p.contactId || null);
    commit('SET_INTERNAL_ACTIVE_CALL_TYPE', callType);
    commit('SET_OUTBOUND_NEEDS_INBOX_SELECTION', needsSelection);
    commit('SET_OUTBOUND_MEDIA_ROUTE', mediaRoute);
    commit(
      'SET_OUTBOUND_SELECTED_SIP_INBOX_ID',
      !isExplicitWhatsappRoute && !needsSelection && sipInboxes?.[0]?.id ? Number(sipInboxes[0].id) : null
    );

    commit('SET_REMOTE_NUMBER', number);
    commit('SET_CALL_DIRECTION', 'outbound');
    commit('SET_CALL_STATE', CALL_STATE.RINGING);
    commit('SET_CALL_UI_MODE', 'modal');
    commit('SET_CALL_MODAL_OPEN', true);
  },

  async CONFIRM_DIAL({ state: $state, getters, dispatch }) {
    const number = ($state.outboundDraftNumber || '').toString().trim();
    if (!number) return;

    const sipInboxes = getters.availableSipInboxes;
    const mediaRoute = $state.outboundMediaRoute;
    const isExplicitWhatsappRoute =
      ($state._activeCallType || 'sip') === 'whatsapp' && mediaRoute?.type === 'whatsapp' && mediaRoute?.inboxId;
    const needsSelection = !isExplicitWhatsappRoute && (Array.isArray(sipInboxes) ? sipInboxes.length : 0) > 1;
    const sipInboxId = $state.outboundSelectedSipInboxId;

    if (needsSelection && !sipInboxId) {
      throw new Error('Selecciona un inbox SIP para realizar la llamada.');
    }

    const selectedSipInboxId = isExplicitWhatsappRoute
      ? null
      : (needsSelection ? sipInboxId : (sipInboxes?.[0]?.id || sipInboxId));

    await dispatch('DIAL', {
      phoneNumber: number,
      contactId: $state.outboundDraftContactId,
      sipInboxId: selectedSipInboxId,
      callType: $state._activeCallType || 'sip',
      mediaRoute,
    });
  },

  CANCEL_DRAFT({ commit }) {
    // Cierra el modal sin tocar SIP (no hay sesión aún)
    commit('SET_OUTBOUND_STAGE', 'idle');
    commit('SET_OUTBOUND_DRAFT_NUMBER', null);
    commit('SET_OUTBOUND_DRAFT_CONTACT_ID', null);
    commit('SET_OUTBOUND_SELECTED_SIP_INBOX_ID', null);
    commit('SET_OUTBOUND_NEEDS_INBOX_SELECTION', false);
    commit('SET_OUTBOUND_MEDIA_ROUTE', null);
    commit('SET_CALL_STATE', CALL_STATE.IDLE);
    commit('SET_CALL_DIRECTION', null);
    commit('SET_REMOTE_NUMBER', null);
    commit('SET_CALL_DURATION', 0);
    commit('SET_MUTED', false);
    commit('SET_CALL_UI_MODE', 'modal');
    commit('SET_CALL_MODAL_OPEN', false);
  },

  MINIMIZE({ commit, state: $state }) {
    if (!isMinimizeEligible($state)) return;
    commit('SET_CALL_UI_MODE', 'minimized');
    commit('SET_CALL_MODAL_OPEN', false);
    toggleInboundRingback($state, 'stopRingback');
  },

  EXPAND({ commit, state: $state }) {
    if ($state.callState === CALL_STATE.IDLE) return;
    commit('SET_CALL_UI_MODE', 'modal');
    commit('SET_CALL_MODAL_OPEN', true);
    toggleInboundRingback($state, 'startRingback');
  },

  async ENSURE_INBOUND_CALL_CONTEXT(
    { commit, dispatch, rootGetters, state: $state },
    { remoteNumber, callType = 'sip', didNumber = null, callId = null }
  ) {
    const number = (remoteNumber || '').toString().trim();
    if (!number) return;
    if (isPbxEventsAuthoritative(rootGetters, rootGetters.getCurrentAccountId)) {
      // PBX ingest owns conversation/ledger; keep local UI state only.
      return;
    }
    const normalizedCallType = (callType || 'sip').toString().trim().toLowerCase() || 'sip';
    // Capture before awaits — call:state idle can clear _activeCallId mid-flight.
    const inboundCallId = (callId || $state._activeCallId || '').toString().trim() || null;

    const contactId = await dispatch('ENSURE_CONTACT_ID', { phoneNumber: number });
    commit('SET_INTERNAL_ACTIVE_CONTACT_ID', contactId);
    commit('SET_INTERNAL_ACTIVE_CALL_TYPE', normalizedCallType);

    if (normalizedCallType === 'whatsapp') {
      commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', null);
      try {
        const accountId = rootGetters.getCurrentAccountId;
        const wootAxios = window?.axios;
        if (!wootAxios) return;

        const allInboxes = rootGetters?.['inboxes/getInboxes'] || [];
        const waInboxes = (Array.isArray(allInboxes) ? allInboxes : []).filter(
          i => i?.channel_type === 'Channel::Whatsapp'
        );
        const didDigits = (didNumber || '').toString().replace(/\D/g, '');
        const matchedByDid = didDigits
          ? waInboxes.filter(i => {
              const inboxDigits = (i?.phone_number || '').toString().replace(/\D/g, '');
              return inboxDigits && inboxDigits === didDigits;
            })
          : [];
        // Línea de negocio (DID / número de inbox WA): si coincide con un inbox, solo ese;
        // si no hay DID o no matchea ningún inbox, se mantiene el comportamiento previo (todos los WA).
        const didScoped = didDigits && matchedByDid.length > 0;
        const whatsappInboxIdArray = (didScoped ? matchedByDid : waInboxes)
          .map(i => Number(i?.id))
          .filter(Boolean);

        if (whatsappInboxIdArray.length === 0) {
          // eslint-disable-next-line no-console
          console.warn('[WSP_CALL_TRACE] inbound:no_whatsapp_inboxes_in_store');
          return;
        }

        if (didDigits && !didScoped) {
          // eslint-disable-next-line no-console
          console.warn('[WSP_CALL_TRACE] inbound:did_no_matching_whatsapp_inbox', {
            didNumber,
            didDigits,
          });
        }

        if (!contactId) {
          // eslint-disable-next-line no-console
          console.warn('[WSP_CALL_TRACE] inbound:missing_contact_id', { number });
          return;
        }

        const searchRes = await wootAxios.get(
          `/api/v1/accounts/${accountId}/contacts/${contactId}/conversations`,
          { params: { 'inbox_id[]': whatsappInboxIdArray } }
        );
        const conversations = searchRes?.data?.payload || [];
        const convosArray = Array.isArray(conversations) ? conversations : [];

        // Spec: WA = contact thread. Reuse open|pending|snoozed; create once if none.
        const reusable = convosArray.filter(c => {
          const status = (c.status || '').toString();
          return status === 'open' || status === 'pending' || status === 'snoozed';
        });
        reusable.sort((a, b) => (Number(b.timestamp) || 0) - (Number(a.timestamp) || 0));

        const activeCallId = inboundCallId;
        let whatsappConvo = reusable[0] || null;
        let createdInboundWhatsappConversation = false;

        if (!whatsappConvo) {
          const singleLineInboxId =
            didScoped && whatsappInboxIdArray.length === 1 ? Number(whatsappInboxIdArray[0]) : null;
          const fallbackInboxId =
            singleLineInboxId ||
            (convosArray[0] && Number(convosArray[0].inbox_id)) ||
            Number(whatsappInboxIdArray[0]);
          if (!fallbackInboxId) {
            // eslint-disable-next-line no-console
            console.warn('[WSP_CALL_TRACE] inbound:no_whatsapp_inbox_for_create', {
              contactId,
              totalResults: convosArray.length,
            });
            return;
          }
          // ContactInbox valida source_id para Channel::Whatsapp con WHATSAPP_CHANNEL_REGEX (/^\d{1,15}$/);
          // el user SIP suele venir como +51966422904 → sin normalizar el POST devuelve 422.
          const whatsappSourceId = number.replace(/\D/g, '');
          if (!whatsappSourceId || whatsappSourceId.length > 15) {
            // eslint-disable-next-line no-console
            console.warn('[WSP_CALL_TRACE] inbound:invalid_wa_source_id', { number, whatsappSourceId });
            return;
          }
          const startedMessage = {
            content: `📞 Llamada entrante de ${number}`,
            private: true,
            content_type: 'call_log',
            content_attributes: {
              call_details: {
                call_type: 'whatsapp',
                provider: 'asterisk18',
                call_id: activeCallId,
                direction: 'inbound',
                from: number,
                to: rootGetters?.getCurrentUser?.sip_extension || null,
                ts: Date.now(),
                result: 'started',
                ...currentAgentStamp(rootGetters, rootGetters?.getCurrentUser?.sip_extension),
              },
            },
          };
          const createRes = await wootAxios.post(`/api/v1/accounts/${accountId}/conversations`, {
            inbox_id: fallbackInboxId,
            contact_id: contactId,
            source_id: whatsappSourceId,
            message: startedMessage,
          });
          whatsappConvo = createRes?.data || null;
          createdInboundWhatsappConversation = !!whatsappConvo?.id;
          if (!createdInboundWhatsappConversation) {
            // eslint-disable-next-line no-console
            console.warn('[WSP_CALL_TRACE] inbound:create_whatsapp_convo_failed', {
              contactId,
              fallbackInboxId,
            });
            return;
          }
        }

        // eslint-disable-next-line no-console
        console.info(
          '[WSP_CALL_TRACE] inbound:find_whatsapp_convo',
          JSON.stringify({
            contactId,
            didNumber: didNumber || null,
            didScoped,
            found: !!whatsappConvo,
            convoId: whatsappConvo?.id || null,
            inboxId: whatsappConvo?.inbox_id || null,
            status: whatsappConvo?.status || null,
            createdNew: createdInboundWhatsappConversation,
            totalResults: convosArray.length,
            reusableMatches: reusable.length,
            whatsappInboxIds: whatsappInboxIdArray,
          })
        );

        const convoId = whatsappConvo.display_id || whatsappConvo.id;
        commit('SET_INTERNAL_ACTIVE_CONVERSATION_ID', convoId);
        commit('SET_INTERNAL_ACTIVE_INBOX_ID', whatsappConvo.inbox_id || null);
        // Hybrid C: early started with Asterisk call_id is the recording resolve/finalize anchor.
        // Meta webhook "started" has wa_call_id only — without this, finalize before PBX ingest loses the URL.
        if (!createdInboundWhatsappConversation && activeCallId) {
          try {
            await wootAxios.post(`/api/v1/accounts/${accountId}/conversations/${convoId}/messages`, {
              content: `📞 Llamada entrante de ${number}`,
              private: true,
              content_type: 'call_log',
              content_attributes: {
                call_details: {
                  call_type: 'whatsapp',
                  provider: 'asterisk18',
                  call_id: activeCallId,
                  direction: 'inbound',
                  from: number,
                  to: rootGetters?.getCurrentUser?.sip_extension || null,
                  ts: Date.now(),
                  result: 'started',
                  ...currentAgentStamp(rootGetters, rootGetters?.getCurrentUser?.sip_extension),
                },
              },
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[WSP_CALL_TRACE] inbound:reuse_started_msg_error', e?.message);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[telephony] no se encontro conversación WhatsApp inbound', {
          message: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
        });
      }
      return;
    }

    const inboxId = await dispatch('ENSURE_SIP_INBOX_ID', { didNumber });
    commit('SET_INTERNAL_ACTIVE_INBOX_ID', inboxId);
    await dispatch('ENSURE_CONVERSATION_ID', {
      phoneNumber: number,
      contactId,
      direction: 'inbound',
      callType: 'sip',
      callId: inboundCallId,
      didNumber,
    });
  },

  async ANSWER() {
    const sipClient = SipClient.getInstance();
    await sipClient.answer();
  },

  async HANGUP({ state: $state, commit, dispatch, rootGetters }) {
    const sipClient = SipClient.getInstance();
    // Modal "Llamando…" sin sesión SIP (falló dial previo): Colgar debe cerrar UI.
    if (!sipClient.currentSession) {
      await dispatch('CANCEL_DRAFT');
      return;
    }

    sipClient.hangup();

    if (
      !isPbxEventsAuthoritative(rootGetters, rootGetters.getCurrentAccountId) &&
      $state._activeCallType === 'whatsapp' &&
      $state.callDirection === 'inbound' &&
      $state._activeConversationId
    ) {
      try {
        const { data } = await CallRecordsApi.reject($state._activeConversationId, {
          id: $state._whatsappCallDbId || undefined,
          wa_call_id: $state._activeWaCallId || undefined,
          call_id: $state._activeCallId || undefined,
        });
        if (data?.whatsapp_call_id) {
          commit('SET_INTERNAL_WHATSAPP_CALL_CONTEXT', {
            whatsappCallDbId: data.whatsapp_call_id,
            whatsappCallConversationId: $state._activeConversationId,
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[WSP_CALL_TRACE] reject:failed', {
          conversationId: $state._activeConversationId,
          error: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
        });
      }
    }
  },

  TOGGLE_MUTE() {
    const sipClient = SipClient.getInstance();
    sipClient.toggleMute();
  },

  PLAY_TRANSFER_RETURN_BEEP() {
    SipClient.getInstance().playTransferReturnBeep();
  },


  async TRANSFER_TO_AGENT({ state: $state, rootGetters, commit, dispatch }, { agentId } = {}) {
    const agents = rootGetters['agents/getAgents'] || [];
    const agent = (Array.isArray(agents) ? agents : []).find(
      a => Number(a.id) === Number(agentId)
    );
    const extension = (agent?.sip_extension || '').toString().trim();
    if (!extension) throw new Error('El agente no tiene extensión SIP');
    const name = (agent?.available_name || agent?.name || 'Agente').toString();
    const sipClient = SipClient.getInstance();

    commit('SET_TRANSFER_TARGET_NAME', name);
    commit('SET_TRANSFER_ERROR_MESSAGE', '');
    commit('SET_TRANSFER_PHASE', 'holding');

    try {
      await sipClient.transferToExtension(extension, {
        metadata: {
          conversationId: $state._activeConversationId,
          callId: $state._activeCallId,
          callType: $state._activeCallType,
          originalCaller: $state.remoteNumber,
          didNumber: $state._activeDidNumber,
          waCallId: $state._activeWaCallId,
        },
        onProgress: ({ phase }) => {
          if (phase === 'ringing') commit('SET_TRANSFER_PHASE', 'transfer_ringing');
          if (phase === 'completing') commit('SET_TRANSFER_PHASE', 'completing');
          if (phase === 'rolling_back') commit('SET_TRANSFER_PHASE', 'rolling_back');
        },
      });
      commit('SET_TRANSFER_PHASE', 'accepted');
      postTransferCallLog({ rootGetters, state: $state, result: 'transferred', targetAgent: agent });
      await new Promise(resolve => setTimeout(resolve, 2000));
      sipClient.finalizeTransferredCall();
      commit('RESET_TRANSFER_STATE');
    } catch (error) {
      if (error?.cause === 'aborted') {
        commit('RESET_TRANSFER_STATE');
        throw error;
      }
      let transferResult = null;
      if (error?.cause === 'rejected') transferResult = 'transfer_rejected';
      else if (error?.cause === 'no_answer' || error?.cause === 'timeout') {
        transferResult = 'transfer_no_answer';
      } else {
        transferResult = 'transfer_failed';
      }
      await postTransferCallLog({
        rootGetters,
        state: $state,
        result: transferResult,
        targetAgent: agent,
      });
      const message =
        error?.cause === 'rejected'
          ? `${name} rechazó la transferencia`
          : error?.cause === 'no_answer' || error?.cause === 'timeout'
            ? `${name} no contestó la transferencia`
            : `No se pudo transferir la llamada a ${name}`;
      commit('SET_TRANSFER_ERROR_MESSAGE', message);
      commit('SET_TRANSFER_PHASE', 'failed');
      dispatch('PLAY_TRANSFER_RETURN_BEEP');
      await new Promise(resolve => setTimeout(resolve, 2500));
      commit('RESET_TRANSFER_STATE');
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

